import { ChevronRight, FileText, RefreshCw } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { AppNavigationLink } from "@/components/app-navigation-link/AppNavigationLink";
import { IndexingStatusIcon } from "@/components/indexing-status/IndexingStatusIcon";
import { HighlightedText } from "@/components/highlighted-text/HighlightedText";
import type { AppNavigationTarget } from "@/lib/navigation/appNavigation";
import { cn, formatBytes } from "@/lib/utils";
import { WorkspaceContentSearchDocument } from "@/models/workspace/WorkspaceContentSearch";
import { WorkspaceDocumentType } from "@/models/workspace/Workspace";
import { ContentMatchIndicator } from "./ContentMatchIndicator";

interface ContentSearchDocumentRowProps {
  document: WorkspaceContentSearchDocument;
  /** The search as typed; it travels into the viewer so the same words are marked there (LAR-264). */
  searchTerm: string;
  /** A kildeklargøring run is in flight for this document. */
  isIndexing?: boolean;
  /** Reruns kildeklargøring; offered only when the document needs it and the user may. */
  onReindex?: (document: WorkspaceContentSearchDocument) => void;
  className?: string;
}

const typeLabel = (type: WorkspaceDocumentType): string | null =>
  type === WorkspaceDocumentType.OTHER ? null : type;

/**
 * One document in the staircase: its name with the matched words marked, the
 * "Fandt X matches" indicator when the match sits in the text only, the shared index
 * marker when the index needs a rerun, and a link that opens the document with the
 * search seeded into the in-document search. A hit with an outdated search index is
 * outlined in the amber of the stale-index notice, so the reader can see which hits the
 * notice counts.
 */
export const ContentSearchDocumentRow = ({
  document,
  searchTerm,
  isIndexing = false,
  onReindex,
  className,
}: ContentSearchDocumentRowProps) => {
  const target: AppNavigationTarget = {
    kind: "workspaceDocument",
    workspaceId: document.workspaceId,
    documentId: document.id,
    docSearch: searchTerm,
  };
  const type = typeLabel(document.documentType);
  const meta = [type, document.size > 0 ? formatBytes(document.size, 1) : null]
    .filter(Boolean)
    .join(" · ");
  const needsReindex = document.searchIndexStale || !document.isIndexed;
  const offerReindex =
    needsReindex &&
    document.canReindex &&
    !isIndexing &&
    onReindex !== undefined;

  const stale = document.searchIndexStale;

  return (
    <div
      data-search-index-stale={stale || undefined}
      className={cn(
        "flex min-w-0 items-center gap-1",
        stale && "rounded-md bg-warning/10 ring-1 ring-warning/50",
        // Room for the rerun button inside the outline; without it the row keeps the same
        // right edge as the rows around it.
        stale && offerReindex && "pr-1",
        className
      )}
    >
      <AppNavigationLink
        to={target}
        className={cn(
          "group flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          stale ? "hover:bg-warning/15" : "hover:bg-muted"
        )}
      >
        <FileText className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <HighlightedText
          text={document.name}
          searchTerm={searchTerm}
          className="min-w-0 truncate"
        />
        {!document.titleMatch && (
          <ContentMatchIndicator
            count={document.contentMatchCount}
            indexStale={document.searchIndexStale}
          />
        )}
        <IndexingStatusIcon
          hideHealthy
          isIndexed={document.isIndexed}
          searchIndexStale={document.searchIndexStale}
          documentType={document.documentType}
          isIndexing={isIndexing}
        />
        <span className="grow" />
        {meta && (
          <span className="hidden shrink-0 text-xs text-muted-foreground md:inline">
            {meta}
          </span>
        )}
        <ChevronRight
          className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
          aria-hidden="true"
        />
      </AppNavigationLink>
      {offerReindex && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 shrink-0 gap-1 px-2 text-xs"
          onClick={() => onReindex(document)}
        >
          <RefreshCw className="size-3.5" aria-hidden="true" />
          Genkør kildeklargøring
        </Button>
      )}
    </div>
  );
};

export default ContentSearchDocumentRow;
